import { TEST_IDS } from '@argus-labs/auth-shared/test-ids'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { InfoIcon } from '@/components/decorative/info-icon'
import { Button } from '@/components/ui/button'
import { PageCard } from '@/components/ui/page-card'
import { ShimmerText } from '@/components/ui/shimmer-text'
import { Text } from '@/components/ui/text'
import { authClient } from '@/lib/auth-client'
import { authFlow, authorizeFlow, completedSearch } from '@/lib/auth-flow'
import { getSessionQueryOptions, invalidateSessionQuery } from '@/lib/session'
import { toast } from '@/lib/toast'
import { cn, commonSearchSchema, generateTitle } from '@/lib/utils'
import { resetPosthog } from '@/telemetry/posthog'

export const Route = createFileRoute('/_auth/authorize')({
  validateSearch: commonSearchSchema,
  loaderDeps: ({ search }) => search,
  component: Authorize,
  head: () => ({ meta: [{ title: generateTitle('Authorize') }] }),
  beforeLoad: ({ search }) => {
    // Only auth handoffs need browser approval.
    if (authFlow(search).kind !== 'handoff') {
      throw redirect({ to: '/account' })
    }
  },
})

type Status = 'waiting_for_user' | 'authorizing' | 'failed'

function Authorize() {
  const { gameId } = Route.useSearch()
  const {
    data: { data },
  } = useSuspenseQuery(getSessionQueryOptions())
  const [status, setStatus] = useState<Status>('waiting_for_user')

  if (!data) return null // should never happen because of beforeLoad in _auth.tsx

  return (
    <PageCard
      title={status === 'failed' ? 'Authorization Failed' : 'Welcome!'}
      description={
        <>
          Sign in to <b>{gameId}</b> with this Argus account.
        </>
      }
    >
      <AuthorizeForm status={status} setStatus={setStatus} />
    </PageCard>
  )
}

function AuthorizeForm({
  status,
  setStatus,
}: {
  status: Status
  setStatus: (status: Status) => void
}) {
  const search = Route.useSearch()
  const { gameId } = search
  const flow = authFlow(search)
  const navigate = useNavigate()

  const handleAuthorize = async () => {
    try {
      setStatus('authorizing')

      // Ensure an artificial delay of 1s (shimmer text)
      const [{ success, error }] = await Promise.all([
        authorizeFlow(flow),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ])

      if (success) {
        return navigate({ to: '/success', search: completedSearch(flow), replace: true })
      }

      setStatus('failed')
      toast.fromError(error)
    } catch {
      setStatus('failed')
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  const handleLogout = async () => {
    const { data, error } = await authClient.signOut()
    if (error || !data.success) return

    invalidateSessionQuery()
    resetPosthog()
    navigate({ to: '/login', search, replace: true })
  }

  const {
    data: { data },
  } = useSuspenseQuery(getSessionQueryOptions())

  if (!data) return null // should never happen

  const {
    user: { name, email },
  } = data

  return (
    <div>
      <UserCard name={name} email={email} />

      <Text block font="beausite" size={12} className="mt-4">
        Continue to return to <b>{gameId}</b> with this account.
      </Text>

      <div className="mt-8">
        {status === 'authorizing' && <ShimmerText>Authorizing…</ShimmerText>}

        {/* The buttons are always rendered (but turned invisible when authorizing to maintain card height) */}
        <div className={cn(status === 'authorizing' && 'invisible pointer-events-none')}>
          <Button type="button" className="mt-8 w-full" onClick={handleAuthorize}>
            Continue as {name}
          </Button>

          <Button
            type="button"
            size="xs"
            className="mt-2 w-full"
            variant="link"
            onClick={handleLogout}
          >
            Log out and continue as another
          </Button>
        </div>
      </div>
    </div>
  )
}

function UserCard({ name, email }: { name: string; email: string }) {
  return (
    <div className="px-4 py-2 bg-brown rounded-lg" data-testid={TEST_IDS.authorizeUserCard}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <Text font="beausite" size={14} color="cream" block>
            {name}
          </Text>
          <Text font="beausite" size={12} color="cream" block>
            {email}
          </Text>
        </div>

        <Link to="/account">
          <InfoIcon className="size-6" />
        </Link>
      </div>
    </div>
  )
}
